import { useCallback, useEffect, useState } from "react";

import { JobCard } from "components";
import { getJobs, Job } from "services/jobs";
import { Container } from "layout";
import { useTenant } from "tenant";
import { pluralize } from "helpers";

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; jobs: Job[] };

const JobBoardPage = () => {
  const { slug, company } = useTenant();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(() => {
    let cancelled = false;
    setState({ status: "loading" });
    getJobs(slug)
      .then((jobs) => !cancelled && setState({ status: "ready", jobs }))
      .catch(() => !cancelled && setState({ status: "error" }));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => load(), [load]);

  return (
    <>
      <div className="bg-orange-200 py-10 text-orange-900 sm:py-16">
        <Container>
          <h1 className="text-3xl font-bold sm:text-5xl lg:text-6xl">
            {company.name ? `Join ${company.name}` : "Join our team"}
          </h1>
          {company.description && (
            <p className="mt-4 max-w-3xl text-base text-orange-900/80 sm:text-lg">
              {company.description}
            </p>
          )}
        </Container>
      </div>
      <Container isPage>
        <section
          aria-labelledby="open-roles"
          className="overflow-hidden rounded-3xl bg-white"
        >
          <header className="border-b border-gray-200 px-6 py-5">
            <h2 id="open-roles" className="font-semibold">
              Open roles
            </h2>
            {state.status === "ready" && state.jobs.length > 0 && (
              <p className="text-sm font-normal text-gray-600">
                {pluralize(state.jobs.length, "open role")}
              </p>
            )}
          </header>
          <div className="px-2 py-4 sm:px-4">
            {state.status === "loading" && (
              <p role="status" className="p-6 text-center text-gray-500">
                Loading open roles…
              </p>
            )}
            {state.status === "error" && (
              <div role="alert" className="p-6 text-center">
                <p className="text-red-600">
                  We couldn't load the open roles. Please try again.
                </p>
                <button
                  type="button"
                  onClick={load}
                  className="mt-4 rounded-lg border border-orange-300 px-4 py-2 text-orange-700 hover:bg-orange-50"
                >
                  Try again
                </button>
              </div>
            )}
            {state.status === "ready" && state.jobs.length === 0 && (
              <p className="p-6 text-center text-gray-600">
                No open roles right now. Please check back soon.
              </p>
            )}
            {state.status === "ready" && state.jobs.length > 0 && (
              <ul className="flex flex-col divide-y divide-gray-100">
                {state.jobs.map((job) => (
                  <JobCard key={job.system.id} job={job} />
                ))}
              </ul>
            )}
          </div>
        </section>
      </Container>
    </>
  );
};

export default JobBoardPage;
